import { memo, useMemo } from 'react';

import { useFilters } from 'hooks/useFilters';
import { useInlineSvgFromUrl } from 'hooks/useInlineSvgFromUrl';
import PropTypes from 'prop-types';

import ToggleFilters from 'components/UI/ToggleFilters/ToggleFilters';
import Typography from 'components/UI/Typography/Typography';
import Section from 'components/pages/Analytics/Layout/Section/Section';
import SectionContent from 'components/pages/Analytics/Layout/SectionContent/SectionContent';
import SectionHeader from 'components/pages/Analytics/Layout/SectionHeader/SectionHeader';
import TopGameCard from 'components/pages/Analytics/TopGames/TopGameCard/TopGameCard';

import { OPTIONS } from 'components/config/analytics';

import styles from './TopGames.module.scss';

const TopGames = ({ title, description, categories, icon }) => {
  const { filters, handleChange } = useFilters({
    type: categories?.[0]?.category_key,
  });

  const inlineIcon = useInlineSvgFromUrl(icon);

  const gameTypes = useMemo(
    () =>
      categories?.map(({ category_key }) =>
        OPTIONS.gameTypes.find(({ value }) => value === category_key),
      ),
    [categories],
  );

  const games = useMemo(
    () =>
      categories?.find(({ category_key }) => category_key === filters.type)
        ?.games,
    [categories, filters.type],
  );

  const gameCardsJSX = useMemo(
    () =>
      games?.map(({ game_code, color, title }) => (
        <TopGameCard
          key={game_code}
          gameCode={game_code}
          strokeColor={color}
          title={title}
          inlineIcon={inlineIcon}
        />
      )),
    [games, inlineIcon],
  );

  if (!categories?.length) return null;

  return (
    <Section className={styles.section}>
      <SectionHeader
        headings={
          <>
            <Typography type="heading-md" weight="medium" uppercase>
              {title}
            </Typography>
            <Typography type="text-lg" color="grey">
              {description}
            </Typography>
          </>
        }
        filters={
          <ToggleFilters
            name="type"
            value={filters.type}
            options={gameTypes}
            onChange={handleChange}
          />
        }
      />
      <SectionContent key={filters.type} type="cards">
        {gameCardsJSX}
      </SectionContent>
    </Section>
  );
};

TopGames.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      category_key: PropTypes.string.isRequired,
      games: PropTypes.arrayOf(
        PropTypes.shape({
          game_code: PropTypes.string.isRequired,
          color: PropTypes.string.isRequired,
          title: PropTypes.string,
        }),
      ).isRequired,
    }),
  ).isRequired,
  icon: PropTypes.string.isRequired,
};

export default memo(TopGames);
